/**
 * DisplayPicker
 *
 * Chip group that lets the admin pick which monitor(s) of the agent
 * to capture. The picker renders one chip per detected display plus a
 * final "Todos" chip (PR 2 = single-pane, so "Todos" is just
 * "show me the agent's primary monitor until multi-pane ships" — the
 * full multi-pane experience is PR 3).
 *
 * The picker is a controlled component: the page owns the selected
 * indices, the picker just renders chips and emits onChange. The page
 * passes the new list to useWebRTC().setDisplays(), which sends the
 * `displays` field in the next SDP offer.
 *
 * Why chips (and not a Select / Menu):
 *  - chips show the monitor hint inline (size + index) without an extra click
 *  - a chip strip is a one-line control that fits the page header on mobile
 *  - "Todos" reads more clearly than a third select option
 *
 * Edge cases:
 *  - agent.displays is undefined/empty → renders nothing (the page
 *    still works against the legacy displayIndex path)
 *  - agent.displaysError is set → renders an Alert instead of chips
 *    (the agent's enumerator is broken, the picker can't help)
 *  - exactly one display → no "Todos" chip (it would be redundant)
 */

import { useCallback, useMemo } from 'react';
import { Box, Chip, Alert, Tooltip, Stack, Typography } from '@mui/material';
import MonitorIcon from '@mui/icons-material/Monitor';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import type { DisplayInfo } from '@/types/agent';

export interface DisplayPickerProps {
  /** Detected displays, in picker render order. */
  displays: DisplayInfo[] | undefined;
  /** Error string from the agent's enumerator (if any). */
  displaysError?: string;
  /** Currently selected display indices. Empty array = no selection. */
  selected: number[];
  /** Called when the user picks a new monitor or the "Todos" chip. */
  onChange: (displays: number[]) => void;
  /** Disable all chips (e.g. while the WebRTC session is connecting). */
  disabled?: boolean;
  /** Compact mode: drops the section label and shrinks spacing. */
  compact?: boolean;
}

/**
 * Format a display's width × height as a short label, e.g. "1920×1080".
 * Falls back to "?"×"?" if either dimension is missing.
 */
function formatSize(d: DisplayInfo): string {
  if (d.width && d.height) return `${d.width}×${d.height}`;
  return '?×?';
}

/**
 * Format the per-monitor "info badges" string that appears next to
 * the size hint. Surfaces the things that matter to operators
 * picking a remote monitor:
 *
 *   - "★"   — primary display (the host's taskbar / Dock / menu
 *              bar). Helps admins pick the right monitor when
 *              they want to interact with the menu bar.
 *   - "150%" — DPI scale (only shown when ≠ 100%; a 100% monitor
 *              in a 100%-DPI setup doesn't need the badge and
 *              showing it would add noise to the common case).
 *   - "↻90" — rotation (only shown when not 0). Most setups
 *              are 0; portrait monitors and tablet docks
 *              report 90/270.
 *   - "0,0" — origin in the virtual desktop (only shown when
 *              non-zero, i.e. the monitor is NOT at the
 *              top-left of the virtual desktop). Helps admins
 *              identify which monitor is "the one to the
 *              right" in a 3+ monitor setup without having to
 *              count chips.
 *
 * All badges are joined by " · " so a typical chip reads
 * "Monitor 1 · 1920×1080 · ★ · 150%". When a display has no
 * interesting metadata, the function returns the size alone.
 */
function formatBadges(d: DisplayInfo): string {
  const parts: string[] = [];
  if (d.primary) parts.push('★');
  if (d.dpiX && d.dpiX !== 96) {
    // DpiX == DpiY on every realistic monitor; show the percent
    // based on DpiX (the convention used by macOS System
    // Settings → Displays and Windows Settings → Display).
    const pct = Math.round((d.dpiX / 96) * 100);
    parts.push(`${pct}%`);
  }
  if (d.rotation && d.rotation !== 0) {
    parts.push(`↻${d.rotation}`);
  }
  if ((d.left || 0) !== 0 || (d.top || 0) !== 0) {
    parts.push(`${d.left || 0},${d.top || 0}`);
  }
  return parts.join(' · ');
}

/**
 * Build the list of "selections" the picker offers, in render order.
 * For N displays this is N+1 entries: one per display + "Todos".
 * When N=1 we skip the "Todos" entry because it would be a no-op.
 */
interface PickerOption {
  key: string;
  label: string;
  hint: string;
  /** Empty array means "all displays" (Todos). */
  displays: number[];
}

function buildOptions(displays: DisplayInfo[]): PickerOption[] {
  // Compose the per-monitor hint: size is mandatory, badges
  // (primary, DPI, rotation, origin) are appended when
  // non-default. The badge string is built by formatBadges so
  // the rules about which badges to show live in one place.
  const out: PickerOption[] = displays.map((d) => {
    const size = formatSize(d);
    const badges = formatBadges(d);
    return {
      key: `d-${d.index}`,
      label: `Monitor ${d.index + 1}`,
      hint: badges ? `${size} · ${badges}` : size,
      displays: [d.index],
    };
  });
  if (displays.length > 1) {
    out.push({
      key: 'all',
      label: 'Todos',
      hint: `${displays.length} pantallas`,
      displays: displays.map((d) => d.index),
    });
  }
  return out;
}

/**
 * Compute the "active" option key for the current selection.
 * - "all" if selected covers every detected display (Todos)
 * - the matching per-display key if selected is a single index
 * - "" if nothing matches (initial state — nothing selected yet)
 */
function activeKey(options: PickerOption[], selected: number[]): string {
  if (selected.length === 0) return '';
  const allIndices = options.filter((o) => o.displays.length > 1).flatMap((o) => o.displays);
  if (selected.length === allIndices.length && allIndices.length > 0) {
    return 'all';
  }
  if (selected.length === 1) {
    const match = options.find((o) => o.displays.length === 1 && o.displays[0] === selected[0]);
    if (match) return match.key;
  }
  return '';
}

export const DisplayPicker: React.FC<DisplayPickerProps> = ({
  displays,
  displaysError,
  selected,
  onChange,
  disabled,
  compact,
}) => {
  const options = useMemo(() => buildOptions(displays ?? []), [displays]);
  const active = useMemo(() => activeKey(options, selected), [options, selected]);

  const handleClick = useCallback(
    (opt: PickerOption) => {
      if (disabled) return;
      // Toggle off: clicking the already-active single-monitor chip
      // would otherwise re-emit the same selection and trigger a
      // useless reconnect. We keep the click idempotent.
      if (opt.key === active) return;
      onChange(opt.displays);
    },
    [disabled, active, onChange]
  );

  if (displaysError) {
    return (
      <Alert severity="warning" sx={{ flexShrink: 0 }}>
        No se pudieron enumerar los monitores del agente: {displaysError}. La sesión usará el
        monitor predeterminado.
      </Alert>
    );
  }

  if (!displays || displays.length === 0) {
    // Nothing to render. The page already shows the legacy single-monitor
    // path via displayIndex; no extra UI is needed here.
    return null;
  }

  return (
    <Box sx={{ flexShrink: 0 }}>
      {!compact && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}
        >
          Captura
        </Typography>
      )}
      <Stack
        direction="row"
        spacing={0.75}
        sx={{ flexWrap: 'wrap', rowGap: 0.75 }}
        role="radiogroup"
        aria-label="Monitor a capturar"
      >
        {options.map((opt) => {
          const isActive = opt.key === active;
          const icon =
            opt.displays.length > 1 ? (
              <DesktopWindowsIcon fontSize="small" />
            ) : (
              <MonitorIcon fontSize="small" />
            );
          return (
            <Tooltip
              key={opt.key}
              title={`${opt.label} (${opt.hint})`}
              placement="top"
              disableHoverListener={isMobileLikeViewport()}
            >
              <span>
                <Chip
                  role="radio"
                  aria-checked={isActive}
                  icon={icon}
                  label={
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}
                    >
                      <span>{opt.label}</span>
                      <Box
                        component="span"
                        sx={{
                          fontSize: '0.7rem',
                          color: isActive ? 'inherit' : 'text.secondary',
                          opacity: isActive ? 0.85 : 0.75,
                        }}
                      >
                        {opt.hint}
                      </Box>
                    </Box>
                  }
                  size="small"
                  color={isActive ? 'primary' : 'default'}
                  variant={isActive ? 'filled' : 'outlined'}
                  onClick={() => handleClick(opt)}
                  disabled={disabled}
                  clickable
                  sx={{
                    cursor: disabled ? 'default' : 'pointer',
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              </span>
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
};

/**
 * Cheap SSR-safe helper: tooltips on the picker chips are mostly noise
 * on touch devices (the chip already shows the size hint). We turn
 * them off when there's no hover capability. Kept in this file so the
 * picker doesn't depend on a media-query hook just for this.
 */
function isMobileLikeViewport(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia('(hover: none)').matches;
  } catch {
    return false;
  }
}
